import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { Link, useNavigate } from 'react-router-dom'
import {
  ShoppingCart, TrendingUp, Activity, Users, AlertTriangle,
  MessageSquare, Flag, Clock, ArrowUp, ArrowDown, Minus, Truck, Send,
  ChevronRight, RefreshCw, Ticket, PartyPopper, Radio,
} from 'lucide-react'
import { api } from '../lib/api'
import { formatCurrency, formatDateTime, STATUS_LABELS, STATUS_COLORS } from '../lib/utils'
import { useAuth } from '../context/AuthContext'
import { OPS } from '../lib/admin-roles'

// ─── Types ───────────────────────────────────────────────────────────

type Delta = { pct: number | null; direction: 'up' | 'down' | 'flat' }

interface DashboardData {
  pulse: {
    today: { orders: number; revenue_fils: number; aov_fils: number }
    yesterday: { orders: number; revenue_fils: number; aov_fils: number }
    delta_orders: Delta
    delta_revenue: Delta
    delta_aov: Delta
    active_deliveries: number
    ambassadors_online: number
    ambassadors_total: number
  }
  action_queue: {
    pending_dispatch: Array<{
      id: string; order_number: string; total_fils: number; created_at: string
      fulfillment: string; payment_method: string; full_name: string; phone: string
      waiting_minutes: number; sla_breached: boolean
    }>
    open_issues: Array<{
      id: string; order_id: string; order_number: string; type: string
      reporter_type: string; notes: string | null; created_at: string; customer_name: string
    }>
    unread_support: number
    sla_breaches: Array<{
      id: string; order_number: string; ambassador_name: string | null
      ambassador_phone: string | null; minutes_elapsed: number
    }>
    low_stock: Array<{
      id: string; sku: string; name_en: string; name_ar: string
      stock: number; image_key: string | null
    }>
    low_stock_threshold: number
  }
  events: {
    upcoming_attendees_7d: number
    revenue_mtd_fils: number
  }
  activity_feed: Array<{
    id: string; order_number: string; total_fils: number; status: string
    created_at: string; updated_at: string; full_name: string; phone: string
  }>
  charts: {
    revenue_14d: { day: string; orders: number; revenue: number }[]
    orders_by_hour: { hour: number; orders: number }[]
    top_products_7d: { id: string; name_en: string; name_ar: string; image_key: string | null; units: number; revenue: number }[]
    top_ambassadors_7d: { id: string; name: string; deliveries: number; earnings: number }[]
  }
}

// ─── Page ────────────────────────────────────────────────────────────

export default function Dashboard() {
  const qc = useQueryClient()
  // Refetch every 30s so the action queue + pulse stay fresh without
  // requiring a manual refresh. Recharts re-rendering is cheap; the
  // single round-trip keeps the bandwidth profile small.
  const { data, isLoading, isError, dataUpdatedAt } = useQuery<DashboardData>({
    queryKey: ['dashboard'],
    queryFn: () => api.get('/dashboard').then(r => r.data),
    refetchInterval: 30_000,
  })

  if (isLoading) return <PageShell title="Dashboard"><div className="text-gray-500 text-sm">Loading…</div></PageShell>
  if (isError || !data) return <PageShell title="Dashboard"><div className="text-red-400 text-sm">Failed to load dashboard data.</div></PageShell>

  return (
    <PageShell
      title="Dashboard"
      lastUpdated={dataUpdatedAt}
      onRefresh={() => qc.invalidateQueries({ queryKey: ['dashboard'] })}
    >
      <PulseStrip pulse={data.pulse} events={data.events} />
      <TwilioHealthStrip />
      <WhatsAppHealthStrip />
      <ActionQueue queue={data.action_queue} onChange={() => qc.invalidateQueries({ queryKey: ['dashboard'] })} />
      <ChartsRow charts={data.charts} />
      <ActivityFeed feed={data.activity_feed} />
    </PageShell>
  )
}

// ─── Tier 1: Pulse ────────────────────────────────────────────────────

function PulseStrip({ pulse, events }: { pulse: DashboardData['pulse']; events: DashboardData['events'] }) {
  return (
    <div className="space-y-3 mb-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <PulseCard
          icon={ShoppingCart}
          label="Orders today"
          value={pulse.today.orders.toString()}
          delta={pulse.delta_orders}
          subtitle={`vs ${pulse.yesterday.orders} yesterday`}
        />
        <PulseCard
          icon={TrendingUp}
          label="Revenue today"
          value={formatCurrency(pulse.today.revenue_fils)}
          delta={pulse.delta_revenue}
          subtitle={`vs ${formatCurrency(pulse.yesterday.revenue_fils)} yesterday`}
        />
        <PulseCard
          icon={Activity}
          label="Average order value"
          value={formatCurrency(pulse.today.aov_fils)}
          delta={pulse.delta_aov}
          subtitle={`vs ${formatCurrency(pulse.yesterday.aov_fils)} yesterday`}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Link
          to="/live-map"
          className="bg-[#1a1a1a] border border-[#2e2e2e] hover:border-[#C41E3A]/50 rounded-xl px-4 py-3 flex items-center justify-between transition-colors group"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#C41E3A]/15 flex items-center justify-center">
              <Truck size={16} className="text-[#C41E3A]" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Active deliveries</p>
              <p className="text-lg font-semibold text-white">{pulse.active_deliveries}</p>
            </div>
          </div>
          <ChevronRight size={16} className="text-gray-600 group-hover:text-[#C41E3A] transition-colors" />
        </Link>
        <Link
          to="/drivers"
          className="bg-[#1a1a1a] border border-[#2e2e2e] hover:border-[#C41E3A]/50 rounded-xl px-4 py-3 flex items-center justify-between transition-colors group"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-green-500/15 flex items-center justify-center">
              <Users size={16} className="text-green-400" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Ambassadors online</p>
              <p className="text-lg font-semibold text-white">
                {pulse.ambassadors_online}
                <span className="text-sm text-gray-500 font-normal"> / {pulse.ambassadors_total}</span>
              </p>
            </div>
          </div>
          <ChevronRight size={16} className="text-gray-600 group-hover:text-[#C41E3A] transition-colors" />
        </Link>
      </div>

      {/* Event-booking heads-up. Two read-only tiles — visit-attendees
          for the next 7 days (drives venue prep) + month-to-date event
          revenue (gives accounting a quick number). Both link into
          /bookings so ops can drill in. */}
      <div className="grid grid-cols-2 gap-3">
        <Link
          to="/bookings"
          className="bg-[#1a1a1a] border border-[#2e2e2e] hover:border-[#C41E3A]/50 rounded-xl px-4 py-3 flex items-center justify-between transition-colors group"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center">
              <Ticket size={16} className="text-amber-400" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Attendees · next 7 days</p>
              <p className="text-lg font-semibold text-white">{events.upcoming_attendees_7d}</p>
            </div>
          </div>
          <ChevronRight size={16} className="text-gray-600 group-hover:text-[#C41E3A] transition-colors" />
        </Link>
        <Link
          to="/bookings"
          className="bg-[#1a1a1a] border border-[#2e2e2e] hover:border-[#C41E3A]/50 rounded-xl px-4 py-3 flex items-center justify-between transition-colors group"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-purple-500/15 flex items-center justify-center">
              <PartyPopper size={16} className="text-purple-400" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Event revenue · MTD</p>
              <p className="text-lg font-semibold text-white">{formatCurrency(events.revenue_mtd_fils)}</p>
            </div>
          </div>
          <ChevronRight size={16} className="text-gray-600 group-hover:text-[#C41E3A] transition-colors" />
        </Link>
      </div>
    </div>
  )
}

// ─── Twilio SMS health strip (ops + super_admin) ─────────────────────
// Compact widget: shows balance + count of messages stuck at "sent"
// for >10 min so ops can catch Jordan carrier A2P filtering the moment
// it starts biting. Links to /sms-log for the full delivery table.
// Renders nothing for roles that don't see /sms-log — avoids clutter
// for marketing / accounting who don't run SMS diagnostics.

interface TwHealth {
  balance?: string
  account?: { status: string; type: string }
  messages?: Array<{ status: string; date_created: string }>
}

function TwilioHealthStrip() {
  const { can } = useAuth()
  const canSee = can(OPS)
  const { data } = useQuery<TwHealth>({
    queryKey: ['twilio-health'],
    queryFn: () => api.get('/twilio-status?page_size=25').then(r => r.data),
    enabled: canSee,
    refetchInterval: 60_000,
    retry: false,
  })

  if (!canSee) return null
  if (!data) return null

  const stuck = (data.messages ?? []).filter(m =>
    m.status === 'sent' &&
    (Date.now() - new Date(m.date_created).getTime()) / 60000 >= 10
  ).length
  const failed = (data.messages ?? []).filter(m =>
    m.status === 'failed' || m.status === 'undelivered'
  ).length

  const alert = stuck > 0 || failed > 0
  const borderClass = alert
    ? 'border-amber-500/40 hover:border-amber-500/70'
    : 'border-[#2e2e2e] hover:border-[#C41E3A]/50'
  const iconClass = alert ? 'bg-amber-500/15 text-amber-400' : 'bg-blue-500/15 text-blue-400'
  const label = alert
    ? `${stuck} stuck · ${failed} failed`
    : 'All delivering'

  return (
    <div className="mb-6">
      <Link
        to="/sms-log"
        className={`bg-[#1a1a1a] border ${borderClass} rounded-xl px-4 py-3 flex items-center justify-between transition-colors group`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${iconClass}`}>
            <Radio size={16} />
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Twilio SMS</p>
            <p className="text-lg font-semibold text-white">
              {data.balance ?? '—'}
              <span className="text-sm text-gray-500 font-normal ml-2">· {label}</span>
            </p>
          </div>
        </div>
        <ChevronRight size={16} className="text-gray-600 group-hover:text-[#C41E3A] transition-colors" />
      </Link>
    </div>
  )
}

// ─── WhatsApp health strip (ops + super_admin) ───────────────────────
// Mirrors the Twilio strip: 24h WhatsApp volume + failed count, linking
// to the Messaging Log's WhatsApp tab. Hidden entirely until there's any
// WhatsApp traffic (avoids a decorative "0 sent" row during rollout).
// Catches the things worth catching: a spike in undeliverable numbers, a
// Meta-paused template (sends suddenly failing), a delivery-rate drop.

interface WaHealth { summary?: { total: number; failed: number; delivered: number } }

function WhatsAppHealthStrip() {
  const { can } = useAuth()
  const canSee = can(OPS)
  const { data } = useQuery<WaHealth>({
    queryKey: ['whatsapp-health'],
    queryFn: () => api.get('/whatsapp-log?hours=24').then(r => r.data),
    enabled: canSee,
    refetchInterval: 60_000,
    retry: false,
  })

  if (!canSee) return null
  const s = data?.summary
  if (!s || !s.total) return null

  const failed = s.failed ?? 0
  const alert = failed > 0
  const borderClass = alert
    ? 'border-amber-500/40 hover:border-amber-500/70'
    : 'border-[#2e2e2e] hover:border-[#25D366]/50'
  const iconClass = alert ? 'bg-amber-500/15 text-amber-400' : 'bg-[#25D366]/15 text-[#25D366]'
  const label = alert ? `${failed} failed · ${s.delivered} delivered` : `${s.delivered} delivered`

  return (
    <div className="mb-6">
      <Link
        to="/sms-log?channel=whatsapp"
        className={`bg-[#1a1a1a] border ${borderClass} rounded-xl px-4 py-3 flex items-center justify-between transition-colors group`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${iconClass}`}>
            <MessageSquare size={16} />
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">WhatsApp (24h)</p>
            <p className="text-lg font-semibold text-white">
              {s.total} sent
              <span className="text-sm text-gray-500 font-normal ml-2">· {label}</span>
            </p>
          </div>
        </div>
        <ChevronRight size={16} className="text-gray-600 group-hover:text-[#25D366] transition-colors" />
      </Link>
    </div>
  )
}

function PulseCard({ icon: Icon, label, value, delta, subtitle }: {
  icon: React.ElementType; label: string; value: string; delta: Delta; subtitle: string
}) {
  const Arrow = delta.direction === 'up' ? ArrowUp : delta.direction === 'down' ? ArrowDown : Minus
  const deltaColor = delta.direction === 'up' ? 'text-green-400'
    : delta.direction === 'down' ? 'text-red-400' : 'text-gray-500'
  return (
    <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-gray-500">
          <Icon size={15} />
          <span className="text-xs uppercase tracking-wider">{label}</span>
        </div>
        <span className={`flex items-center gap-0.5 text-xs font-medium ${deltaColor}`}>
          <Arrow size={11} />
          {delta.pct === null ? '—' : `${Math.abs(delta.pct).toFixed(1)}%`}
        </span>
      </div>
      <p className="text-2xl font-semibold text-white mb-1">{value}</p>
      <p className="text-[11px] text-gray-600">{subtitle}</p>
    </div>
  )
}

// ─── Tier 2: Action Queue ────────────────────────────────────────────

function ActionQueue({ queue, onChange }: { queue: DashboardData['action_queue']; onChange: () => void }) {
  const nothingToDo =
    queue.pending_dispatch.length === 0 &&
    queue.open_issues.length === 0 &&
    queue.unread_support === 0 &&
    queue.sla_breaches.length === 0 &&
    queue.low_stock.length === 0

  return (
    <section className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-white">Action queue</h2>
        {nothingToDo && (
          <span className="text-xs text-green-400">Inbox zero — nothing waiting.</span>
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <PendingDispatch orders={queue.pending_dispatch} onChange={onChange} />
        <div className="space-y-3">
          <OpenIssuesCard issues={queue.open_issues} />
          <div className="grid grid-cols-2 gap-3">
            <SmallCountCard
              icon={MessageSquare}
              label="Unread support"
              count={queue.unread_support}
              to="/support"
              accent="text-blue-400"
              accentBg="bg-blue-500/15"
            />
            <SmallCountCard
              icon={Clock}
              label="SLA breaches"
              count={queue.sla_breaches.length}
              to="/live-map"
              accent="text-amber-400"
              accentBg="bg-amber-500/15"
            />
          </div>
          <LowStockCard items={queue.low_stock} threshold={queue.low_stock_threshold} />
        </div>
      </div>
    </section>
  )
}

function PendingDispatch({ orders, onChange }: { orders: DashboardData['action_queue']['pending_dispatch']; onChange: () => void }) {
  const [confirming, setConfirming] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  async function confirmOrder(orderId: string) {
    setConfirming(orderId)
    setError(null)
    try {
      // PATCH /admin/orders/:id/status with status='confirmed' triggers
      // the server's tiered dispatch (see backend/worker/src/lib/dispatch.ts).
      // Ambassadors get a push within the same tick.
      await api.patch(`/orders/${orderId}/status`, { status: 'confirmed' })
      onChange()
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } }
      setError(axiosErr?.response?.data?.message ?? 'Failed to confirm')
    } finally {
      setConfirming(null)
    }
  }

  return (
    <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#2e2e2e]">
        <div className="flex items-center gap-2">
          <ShoppingCart size={14} className="text-[#C41E3A]" />
          <h3 className="text-sm font-medium text-white">Pending orders</h3>
          {orders.length > 0 && (
            <span className="text-[10px] bg-[#C41E3A]/15 text-[#C41E3A] px-1.5 py-0.5 rounded-full font-medium">
              {orders.length}
            </span>
          )}
        </div>
        <Link to="/orders" className="text-[11px] text-gray-500 hover:text-white flex items-center gap-1">
          All orders <ChevronRight size={11} />
        </Link>
      </div>
      {orders.length === 0 ? (
        <div className="px-4 py-8 text-center text-xs text-gray-600">
          No orders waiting — everything's been dispatched.
        </div>
      ) : (
        <div className="divide-y divide-[#222] max-h-[420px] overflow-y-auto">
          {orders.map(o => (
            <div key={o.id} className={`px-4 py-3 hover:bg-white/2 ${o.sla_breached ? 'bg-red-500/5' : ''}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <button
                      type="button"
                      onClick={() => navigate(`/orders?id=${o.id}`)}
                      className="text-xs font-mono text-gray-300 hover:text-white"
                    >
                      {o.order_number}
                    </button>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                      o.sla_breached ? 'bg-red-500/20 text-red-400' : 'bg-gray-500/15 text-gray-400'
                    }`}>
                      {o.waiting_minutes}m waiting
                    </span>
                    {o.fulfillment === 'pickup' && (
                      <span className="text-[10px] bg-purple-500/15 text-purple-400 px-1.5 py-0.5 rounded-full">
                        Pickup
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 truncate">
                    {o.full_name} · {formatCurrency(o.total_fils)} · {o.payment_method}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => confirmOrder(o.id)}
                  disabled={confirming === o.id || o.fulfillment === 'pickup'}
                  title={o.fulfillment === 'pickup' ? 'Pickup orders don\'t need dispatch' : 'Confirm & dispatch to drivers'}
                  className="flex items-center gap-1 px-2.5 py-1.5 bg-[#C41E3A] hover:bg-[#a51830] disabled:bg-gray-700 disabled:text-gray-500 text-white text-[11px] font-medium rounded-md transition-colors whitespace-nowrap"
                >
                  <Send size={11} />
                  {confirming === o.id ? 'Sending…' : 'Confirm'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {error && <div className="px-4 py-2 text-xs text-red-400 border-t border-red-500/30">{error}</div>}
    </div>
  )
}

function OpenIssuesCard({ issues }: { issues: DashboardData['action_queue']['open_issues'] }) {
  return (
    <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#2e2e2e]">
        <div className="flex items-center gap-2">
          <Flag size={14} className="text-amber-400" />
          <h3 className="text-sm font-medium text-white">Open issues</h3>
          {issues.length > 0 && (
            <span className="text-[10px] bg-amber-500/15 text-amber-400 px-1.5 py-0.5 rounded-full font-medium">
              {issues.length}
            </span>
          )}
        </div>
        <Link to="/issues" className="text-[11px] text-gray-500 hover:text-white flex items-center gap-1">
          All issues <ChevronRight size={11} />
        </Link>
      </div>
      {issues.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-gray-600">No open issues.</div>
      ) : (
        <ul className="divide-y divide-[#222] max-h-[180px] overflow-y-auto">
          {issues.slice(0, 5).map(i => (
            <li key={i.id}>
              <Link to={`/orders?id=${i.order_id}`} className="block px-4 py-2.5 hover:bg-white/2">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-mono text-gray-300">{i.order_number}</span>
                  <span className="text-[10px] uppercase tracking-wider text-amber-400">{i.type}</span>
                  <span className="text-[10px] text-gray-600 capitalize">{i.reporter_type}</span>
                </div>
                <p className="text-[11px] text-gray-500 truncate">
                  {i.customer_name}
                  {i.notes ? ` — ${i.notes}` : ''}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function SmallCountCard({ icon: Icon, label, count, to, accent, accentBg }: {
  icon: React.ElementType; label: string; count: number; to: string; accent: string; accentBg: string
}) {
  return (
    <Link
      to={to}
      className="bg-[#1a1a1a] border border-[#2e2e2e] hover:border-[#C41E3A]/50 rounded-xl px-4 py-3 flex items-center justify-between transition-colors group"
    >
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-lg ${accentBg} flex items-center justify-center`}>
          <Icon size={14} className={accent} />
        </div>
        <div>
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
          <p className="text-lg font-semibold text-white leading-tight">{count}</p>
        </div>
      </div>
      <ChevronRight size={14} className="text-gray-600 group-hover:text-[#C41E3A] transition-colors" />
    </Link>
  )
}

function LowStockCard({ items, threshold }: { items: DashboardData['action_queue']['low_stock']; threshold: number }) {
  return (
    <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#2e2e2e]">
        <div className="flex items-center gap-2">
          <AlertTriangle size={14} className="text-amber-400" />
          <h3 className="text-sm font-medium text-white">Low stock</h3>
          {items.length > 0 && (
            <span className="text-[10px] bg-amber-500/15 text-amber-400 px-1.5 py-0.5 rounded-full font-medium">
              {items.length}
            </span>
          )}
          <span className="text-[10px] text-gray-600">≤ {threshold}</span>
        </div>
        <Link to="/products" className="text-[11px] text-gray-500 hover:text-white flex items-center gap-1">
          Products <ChevronRight size={11} />
        </Link>
      </div>
      {items.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-gray-600">All products well-stocked.</div>
      ) : (
        <ul className="divide-y divide-[#222] max-h-[180px] overflow-y-auto">
          {items.slice(0, 6).map(p => (
            <li key={p.id}>
              <Link to={`/products`} className="flex items-center justify-between px-4 py-2 hover:bg-white/2">
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-white truncate">{p.name_en}</p>
                  <p className="text-[10px] text-gray-600">{p.sku}</p>
                </div>
                <span className={`text-xs font-mono ${p.stock === 0 ? 'text-red-400' : 'text-amber-400'}`}>
                  {p.stock}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ─── Tier 4: Charts ──────────────────────────────────────────────────

function ChartsRow({ charts }: { charts: DashboardData['charts'] }) {
  const revenue14d = charts.revenue_14d.map(d => ({ ...d, revenue_jd: d.revenue / 1000 }))
  return (
    <section className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-3">
      {/* Revenue 14d */}
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl p-5">
        <h3 className="text-sm font-medium text-gray-300 mb-4">Revenue · last 14 days (JD)</h3>
        <ResponsiveContainer width="100%" height={180}>
          <AreaChart data={revenue14d}>
            <defs>
              <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#C41E3A" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#C41E3A" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="day" tick={{ fill: '#666', fontSize: 10 }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fill: '#666', fontSize: 10 }} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 8, fontSize: 12 }}
              labelStyle={{ color: '#aaa' }}
              itemStyle={{ color: '#C41E3A' }}
              formatter={(value) => [`JD ${Number(value).toFixed(3)}`, 'Revenue']}
            />
            <Area type="monotone" dataKey="revenue_jd" stroke="#C41E3A" strokeWidth={2} fill="url(#revGrad)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Orders by hour today */}
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl p-5">
        <h3 className="text-sm font-medium text-gray-300 mb-4">Orders by hour · today</h3>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={charts.orders_by_hour}>
            <XAxis dataKey="hour" tick={{ fill: '#666', fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={(h) => `${h}:00`} />
            <YAxis tick={{ fill: '#666', fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip
              contentStyle={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 8, fontSize: 12 }}
              labelStyle={{ color: '#aaa' }}
              itemStyle={{ color: '#C41E3A' }}
              labelFormatter={(h) => `${h}:00`}
            />
            <Bar dataKey="orders" fill="#C41E3A" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Top products 7d */}
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl p-5">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Top products · last 7 days</h3>
        {charts.top_products_7d.length === 0 ? (
          <p className="text-xs text-gray-600 py-6 text-center">No sales in the last 7 days.</p>
        ) : (
          <ul className="space-y-2">
            {charts.top_products_7d.map((p, i) => (
              <li key={p.id} className="flex items-center justify-between gap-3 py-1.5 border-b border-[#222] last:border-0">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="text-[10px] text-gray-600 font-mono w-4">{i + 1}.</span>
                  <span className="text-xs text-white truncate">{p.name_en}</span>
                </div>
                <div className="text-right whitespace-nowrap">
                  <p className="text-xs text-white font-medium">{p.units} <span className="text-gray-600 font-normal">units</span></p>
                  <p className="text-[10px] text-gray-500">{formatCurrency(p.revenue)}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Top ambassadors 7d */}
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl p-5">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Top ambassadors · last 7 days</h3>
        {charts.top_ambassadors_7d.length === 0 ? (
          <p className="text-xs text-gray-600 py-6 text-center">No deliveries completed.</p>
        ) : (
          <ul className="space-y-2">
            {charts.top_ambassadors_7d.map((a, i) => (
              <li key={a.id} className="flex items-center justify-between gap-3 py-1.5 border-b border-[#222] last:border-0">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="text-[10px] text-gray-600 font-mono w-4">{i + 1}.</span>
                  <span className="text-xs text-white truncate">{a.name}</span>
                </div>
                <div className="text-right whitespace-nowrap">
                  <p className="text-xs text-white font-medium">{a.deliveries} <span className="text-gray-600 font-normal">deliveries</span></p>
                  <p className="text-[10px] text-gray-500">{formatCurrency(a.earnings)}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}

// ─── Tier 3: Activity Feed ───────────────────────────────────────────

function ActivityFeed({ feed }: { feed: DashboardData['activity_feed'] }) {
  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-white">Recent activity</h2>
        <Link to="/orders" className="text-xs text-gray-500 hover:text-white flex items-center gap-1">
          All orders <ChevronRight size={12} />
        </Link>
      </div>
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-600 border-b border-[#2e2e2e]">
              <th className="px-4 py-2.5 text-left font-medium">Order</th>
              <th className="px-4 py-2.5 text-left font-medium">Customer</th>
              <th className="px-4 py-2.5 text-left font-medium">Total</th>
              <th className="px-4 py-2.5 text-left font-medium">Status</th>
              <th className="px-4 py-2.5 text-left font-medium">Last update</th>
            </tr>
          </thead>
          <tbody>
            {feed.map(o => (
              <tr key={o.id} className="border-b border-[#222] hover:bg-white/2 transition-colors">
                <td className="px-4 py-2.5">
                  <Link to={`/orders?id=${o.id}`} className="font-mono text-xs text-gray-300 hover:text-white">
                    {o.order_number}
                  </Link>
                </td>
                <td className="px-4 py-2.5">
                  <p className="text-white text-xs">{o.full_name}</p>
                  <p className="text-gray-500 text-[10px]">{o.phone}</p>
                </td>
                <td className="px-4 py-2.5 text-gray-300 text-xs">{formatCurrency(o.total_fils)}</td>
                <td className="px-4 py-2.5">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[o.status] ?? ''}`}>
                    {STATUS_LABELS[o.status] ?? o.status}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-gray-500 text-[11px]">{formatDateTime(o.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

// ─── Page shell ──────────────────────────────────────────────────────

function PageShell({ title, lastUpdated, onRefresh, children }: {
  title: string; lastUpdated?: number; onRefresh?: () => void; children: React.ReactNode
}) {
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-lg font-semibold text-white">{title}</h1>
        {onRefresh && (
          <div className="flex items-center gap-3">
            {lastUpdated && (
              <span className="text-[10px] text-gray-600">
                Updated {formatRelative(lastUpdated)}
              </span>
            )}
            <button
              onClick={onRefresh}
              title="Refresh now"
              className="p-1.5 text-gray-500 hover:text-white hover:bg-[#2e2e2e] rounded-md transition-colors"
            >
              <RefreshCw size={13} />
            </button>
          </div>
        )}
      </div>
      {children}
    </div>
  )
}

function formatRelative(ts: number): string {
  const seconds = Math.floor((Date.now() - ts) / 1000)
  if (seconds < 5) return 'just now'
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ago`
}
